import { FieldErrors } from 'react-hook-form';
import { UserRolesEnum } from '@shared/constants/auth.constants';
import { CampaignRunStatusType } from '@shared/services/apiService/missing-types';
import { SidebarSubTabFieldsMap, SidebarTabNames } from '@pages/Admin/Campaign/ui/constants';
import { SidebarTabNamesType } from '@pages/Admin/Campaign/ui/types';
import { CreateCampaignFormValue } from '../model/type';

export interface DisabledReasonParams {
  campaign: { customers?: unknown[]; agents?: unknown[]; squad?: { agents?: unknown[] } };
  isRunningLoading: boolean;
  isStoppingLoading: boolean;
  currentUserRole?: string;
}

export const getCampaignDisabledReasonKey = ({
  campaign,
  isRunningLoading,
  isStoppingLoading,
  currentUserRole,
}: DisabledReasonParams): string => {
  if (isRunningLoading) {
    return 'adminMainPage.campaigns.validation.campaignStarting';
  }
  if (isStoppingLoading) {
    return 'adminMainPage.campaigns.validation.campaignStopping';
  }
  if (!campaign.customers?.length) {
    return 'adminMainPage.campaigns.validation.noPhoneNumbers';
  }
  if (!(campaign.agents?.length || campaign.squad?.agents?.length)) {
    return 'adminMainPage.campaigns.validation.noAgents';
  }
  if (currentUserRole === UserRolesEnum.VIEWER) {
    return 'adminMainPage.campaigns.validation.noPermissionToStart';
  }
  return 'adminMainPage.operation.startCampaign';
};

export type CampaignValidationMessage = {
  key: string;
  params?: Record<string, string>;
};

export type CampaignStartBlocker = {
  tab?: SidebarTabNamesType;
  message: CampaignValidationMessage;
  isFormField?: boolean;
};

type TranslateFn = (key: string, params?: Record<string, string>) => string;

const FIELD_MESSAGE_KEYS: Record<string, string> = {
  agents: 'adminMainPage.errors.atLeastOneAgent',
  title: 'adminMainPage.campaigns.validation.titleRequiredForStart',
  callSchedule: 'adminMainPage.campaigns.validation.scheduleRequiredForStart',
  retryIntervalInMinutes: 'adminMainPage.campaigns.validation.retryFieldRequiredForStart',
  retryMaxNumber: 'adminMainPage.campaigns.validation.retryFieldRequiredForStart',
  retriesPerDay: 'adminMainPage.campaigns.validation.retryFieldRequiredForStart',
  reservedLimit: 'adminMainPage.campaigns.validation.reservedLimitRequiredForStart',
  'analysisPlan.summaryPrompt': 'adminMainPage.campaigns.validation.analysisPromptRequiredForStart',
  followUpTemplate: 'adminMainPage.campaigns.validation.followUpTemplateRequiredForStart',
  leadList: 'adminMainPage.campaigns.validation.noPhoneNumbers',
};

const FIELD_LABEL_KEYS: Record<string, string> = {
  retryIntervalInMinutes: 'campaignPage.retryDelay',
  retryMaxNumber: 'campaignPage.overallRetryAttempt',
  retriesPerDay: 'campaignPage.retryPerDay',
};

export const flattenFormErrorFields = (errors: FieldErrors, prefix = ''): string[] => {
  const fields: string[] = [];

  for (const key of Object.keys(errors)) {
    const path = prefix ? `${prefix}.${key}` : key;
    const error = errors[key as keyof typeof errors];

    if (!error) {
      continue;
    }

    if (typeof error === 'object' && 'type' in error && error.type) {
      fields.push(path);
    } else if (typeof error === 'object') {
      fields.push(...flattenFormErrorFields(error as FieldErrors, path));
    }
  }

  return fields;
};

export const getTabForField = (field: string): SidebarTabNamesType | undefined => {
  const tab = SidebarSubTabFieldsMap[field] ?? SidebarSubTabFieldsMap[field.split('.')[0]];
  return tab as SidebarTabNamesType | undefined;
};

export const getFieldBlockerMessage = (field: string): CampaignValidationMessage => {
  const key = FIELD_MESSAGE_KEYS[field] ?? 'adminMainPage.errors.fillTheField';
  const fieldLabelKey = FIELD_LABEL_KEYS[field];

  return {
    key,
    ...(fieldLabelKey ? { params: { fieldName: fieldLabelKey } } : {}),
  };
};

export interface CampaignStartBlockersParams {
  isRunningLoading: boolean;
  isLoading: boolean;
  isLoadingCampaignAgents: boolean;
  isStoppingLoading: boolean;
  campaignStatus?: string;
  hasAgents: boolean;
  hasConfiguredPhoneNumbers: boolean;
  currentUserRole?: string;
  formErrors: FieldErrors<CreateCampaignFormValue>;
}

export const getCampaignStartBlockers = ({
  isRunningLoading,
  isLoading,
  isLoadingCampaignAgents,
  isStoppingLoading,
  campaignStatus,
  hasAgents,
  hasConfiguredPhoneNumbers,
  currentUserRole,
  formErrors,
}: CampaignStartBlockersParams): CampaignStartBlocker[] => {
  if (isRunningLoading) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.campaignStarting' } }];
  }

  if (isLoading || isLoadingCampaignAgents) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.loadingCampaignData' } }];
  }

  if (isStoppingLoading) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.campaignStopping' } }];
  }

  if (campaignStatus === CampaignRunStatusType.FINISHING) {
    return [{ message: { key: 'adminMainPage.campaigns.validation.campaignFinishing' } }];
  }

  const blockers: CampaignStartBlocker[] = [];

  if (!hasAgents) {
    blockers.push({
      tab: SidebarTabNames.AGENT,
      message: { key: 'adminMainPage.errors.atLeastOneAgent' },
    });
  }

  if (!hasConfiguredPhoneNumbers) {
    blockers.push({
      tab: SidebarTabNames.PHONE_NUMBERS,
      message: { key: 'adminMainPage.campaigns.validation.noPhoneNumbers' },
    });
  }

  const errorFields = flattenFormErrorFields(formErrors);

  for (const field of errorFields) {
    if (field === 'agents' && !hasAgents) {
      continue;
    }

    blockers.push({
      tab: getTabForField(field),
      message: getFieldBlockerMessage(field),
      isFormField: true,
    });
  }

  if (currentUserRole === UserRolesEnum.VIEWER) {
    blockers.push({ message: { key: 'adminMainPage.campaigns.validation.noPermissionToStart' } });
  }

  return blockers;
};

export const translateBlockerMessage = (blocker: CampaignStartBlocker, t: TranslateFn): string => {
  const params = blocker.message.params
    ? Object.fromEntries(
        Object.entries(blocker.message.params).map(([key, value]) => [
          key,
          key === 'fieldName' ? t(value) : value,
        ]),
      )
    : undefined;

  return t(blocker.message.key, params);
};

export const getPrimaryDisabledReason = (
  blockers: CampaignStartBlocker[],
  t: TranslateFn,
): string => {
  if (!blockers.length) {
    return '';
  }

  const first = blockers[0];
  const isOperationalBlocker =
    !first.isFormField &&
    (!first.tab ||
      first.tab === SidebarTabNames.AGENT ||
      first.tab === SidebarTabNames.PHONE_NUMBERS);

  if (isOperationalBlocker) {
    return translateBlockerMessage(first, t);
  }

  const formFieldBlockers = blockers.filter((blocker) => blocker.isFormField);

  if (formFieldBlockers.length > 1) {
    const issues = formFieldBlockers
      .map((blocker) => translateBlockerMessage(blocker, t))
      .join('; ');
    return t('adminMainPage.campaigns.validation.cannotStartFixIssues', { issues });
  }

  return translateBlockerMessage(first, t);
};

export const getTabIssueMessages = (
  blockers: CampaignStartBlocker[],
  t: TranslateFn,
): Partial<Record<SidebarTabNamesType, string>> => {
  const byTab: Partial<Record<SidebarTabNamesType, string[]>> = {};

  for (const blocker of blockers) {
    if (!blocker.tab) {
      continue;
    }

    const message = translateBlockerMessage(blocker, t);

    if (!byTab[blocker.tab]) {
      byTab[blocker.tab] = [];
    }

    if (!byTab[blocker.tab]!.includes(message)) {
      byTab[blocker.tab]!.push(message);
    }
  }

  return Object.fromEntries(
    Object.entries(byTab).map(([tab, messages]) => [tab, (messages ?? []).join('; ')]),
  ) as Partial<Record<SidebarTabNamesType, string>>;
};

export const getIssuedTabsFromBlockers = (
  blockers: CampaignStartBlocker[],
): SidebarTabNamesType[] => {
  const tabs = blockers
    .map((blocker) => blocker.tab)
    .filter((tab): tab is SidebarTabNamesType => Boolean(tab));
  return [...new Set(tabs)];
};
